import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { sendWithEncryptedPasswords } from '@/lib/credentialEncryption';

const BFF_URL = import.meta.env.VITE_BFF_URL || '/api';

// Separate axios instance for platform admin requests — no X-Tenant-Id header
export const platformApiClient: AxiosInstance = axios.create({
  baseURL: BFF_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor — uses platform tokens
platformApiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('platformAccessToken');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => Promise.reject(error)
);

// Response interceptor — refresh platform token on 401
platformApiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken = localStorage.getItem('platformRefreshToken');
      if (refreshToken) {
        try {
          const response = await axios.post(`${BFF_URL}/admin/auth/refresh`, { refreshToken });
          const { accessToken, refreshToken: newRefreshToken } = response.data;
          localStorage.setItem('platformAccessToken', accessToken);
          localStorage.setItem('platformRefreshToken', newRefreshToken);

          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          }
          return platformApiClient(originalRequest);
        } catch {
          localStorage.removeItem('platformAccessToken');
          localStorage.removeItem('platformRefreshToken');
          window.location.href = '/platform-admin';
          return Promise.reject(error);
        }
      }
    }

    return Promise.reject(error);
  }
);

export interface PlatformLoginDto {
  email: string;
  password: string;
}

export interface PlatformUser {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  displayName?: string;
  avatarUrl?: string;
  platformRole: string;
  /** Main Super Admin: may manage platform admins and registration tokens. */
  isPrimary?: boolean;
  mfaEnabled: boolean;
  status: string;
}

export interface PlatformRegistrationStart {
  maskedEmail?: string;
  expiresInSeconds?: number;
  emailSent?: boolean;
}

export interface PlatformRegistrationCheck {
  email: string;
  registrationType: 'setup' | 'invite';
}

export interface PlatformAuthResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  user: PlatformUser;
  requiresMfa?: boolean;
  mfaToken?: string;
  maskedEmail?: string;
  expiresInSeconds?: number;
}

class PlatformAuthService {
  private readonly basePath = '/admin/auth';

  async login(dto: PlatformLoginDto): Promise<PlatformAuthResponse> {
    const response = await sendWithEncryptedPasswords(dto, (body) =>
      platformApiClient.post<PlatformAuthResponse>(`${this.basePath}/login`, body),
    );
    const data = response.data;

    // Only persist tokens on a completed login — the OTP-challenge response
    // carries no tokens.
    if (data.accessToken) {
      localStorage.setItem('platformAccessToken', data.accessToken);
      localStorage.setItem('platformRefreshToken', data.refreshToken);
    }

    return data;
  }

  // Step 2 — verify the emailed OTP and complete platform login.
  async verifyLoginOtp(mfaToken: string, code: string): Promise<PlatformAuthResponse> {
    const response = await platformApiClient.post<PlatformAuthResponse>(
      `${this.basePath}/mfa/verify-login`,
      { mfaToken, code },
    );
    const data = response.data;
    if (data.accessToken) {
      localStorage.setItem('platformAccessToken', data.accessToken);
      localStorage.setItem('platformRefreshToken', data.refreshToken);
    }
    return data;
  }

  // Request a fresh code after the 90s window lapses.
  async resendLoginOtp(mfaToken: string): Promise<PlatformAuthResponse> {
    const response = await platformApiClient.post<PlatformAuthResponse>(
      `${this.basePath}/mfa/resend`,
      { mfaToken },
    );
    return response.data;
  }

  // Registration step 1: email + registration token. Emails a verification link.
  async startRegistration(email: string, token: string): Promise<PlatformRegistrationStart> {
    const response = await platformApiClient.post<PlatformRegistrationStart>(
      '/admin/onboarding/register/start',
      { email, token },
    );
    return response.data;
  }

  // Registration step 2: check the emailed link (does not use it up).
  async verifyRegistration(verificationToken: string): Promise<PlatformRegistrationCheck> {
    const response = await platformApiClient.post<PlatformRegistrationCheck>(
      '/admin/onboarding/register/verify',
      { verificationToken },
    );
    return response.data;
  }

  // Registration step 3: name + password. Creates the account and signs in.
  async completeRegistration(dto: {
    verificationToken: string;
    firstName: string;
    lastName: string;
    password: string;
  }): Promise<PlatformAuthResponse> {
    const response = await sendWithEncryptedPasswords(dto, (body) =>
      platformApiClient.post<PlatformAuthResponse>('/admin/onboarding/register/complete', body),
    );
    const data = response.data;
    if (data.accessToken) {
      localStorage.setItem('platformAccessToken', data.accessToken);
      localStorage.setItem('platformRefreshToken', data.refreshToken);
    }
    return data;
  }

  async logout(): Promise<void> {
    try {
      await platformApiClient.post(`${this.basePath}/logout`);
    } finally {
      localStorage.removeItem('platformAccessToken');
      localStorage.removeItem('platformRefreshToken');
    }
  }

  async getCurrentUser(): Promise<PlatformUser> {
    const response = await platformApiClient.get<PlatformUser>('/admin/users/me');
    return response.data;
  }

  // Self-service profile update (e.g. changing the login email). Uses the
  // self-service /me endpoint (not role-gated) so any platform user can edit
  // their own record; the backend derives the id from the token.
  async updateProfile(
    data: { email?: string; firstName?: string; lastName?: string; displayName?: string },
  ): Promise<PlatformUser> {
    const response = await platformApiClient.put<PlatformUser>('/admin/users/me', data);
    return response.data;
  }

  // Self-service password change. Backend verifies the current password.
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await sendWithEncryptedPasswords({ currentPassword, newPassword }, (body) =>
      platformApiClient.post('/admin/users/me/change-password', body),
    );
  }

  // Email change, step 1: request a code sent to the NEW address.
  async requestEmailChange(
    email: string,
  ): Promise<{ emailChangeToken: string; maskedEmail?: string; expiresInSeconds?: number }> {
    const response = await platformApiClient.post('/admin/users/me/email-change/request', { email });
    return response.data;
  }

  // Email change, step 2: verify the emailed code and apply the change.
  async verifyEmailChange(emailChangeToken: string, code: string): Promise<void> {
    await platformApiClient.post('/admin/users/me/email-change/verify', { emailChangeToken, code });
  }

  isAuthenticated(): boolean {
    return !!localStorage.getItem('platformAccessToken');
  }
}

export const platformAuthService = new PlatformAuthService();
